<script setup lang="ts">
import {computed, inject} from 'vue'
import {
  DataColumnValuesEnumSet,
  DataColumnValuesEnumValue,
  EnumsBar,
  IconClose,
  TextInput
} from '@hantera/design-system'
import {useLocalization} from '@hantera/portal-app'
import PermissionPicker from '../Shared/PermissionPicker.vue'
import type {useClientStore} from '@/stores/iam/client-store'

const t = useLocalization('iam.clients')
const store = inject<ReturnType<typeof useClientStore>>('clientStore')!

const client = computed(() => store.client)
const canWrite = computed(() => store.canWrite)

const displayScopes = computed({
  get: () => store.pendingScopes ?? client.value?.scopes ?? [],
  set: (value: string[]) => {
    store.pendingScopes = value
  },
})

const displayGrantTypes = computed({
  get: () => {
    const pending = store.pendingGrantTypes
    if (pending !== null) return pending
    return client.value?.grantTypes?.filter(gt => gt !== 'client_credentials') ?? []
  },
  set: (value: string[]) => {
    store.pendingGrantTypes = value
  },
})

const displayRedirectUris = computed({
  get: () => store.pendingRedirectUris ?? client.value?.redirectUris ?? [],
  set: (value: string[]) => {
    store.pendingRedirectUris = value
  },
})

const enableUserAuth = computed(() => {
  const grantTypes = displayGrantTypes.value
  return grantTypes.length > 0
})

function addRedirectUri() {
  const current = displayRedirectUris.value
  displayRedirectUris.value = [...current, '']
}

function removeRedirectUri(index: number) {
  const current = [...displayRedirectUris.value]
  current.splice(index, 1)
  displayRedirectUris.value = current
}

function updateRedirectUri(index: number, value: string) {
  const current = [...displayRedirectUris.value]
  current[index] = value
  displayRedirectUris.value = current
}

const grantTypeLabels = {
  authorization_code: t('userAuth.grantTypes.authorizationCode'),
  implicit: t('userAuth.grantTypes.implicit'),
  refresh_token: t('userAuth.grantTypes.refreshToken'),
}

const availableGrantTypes = computed(() => {
  const set: DataColumnValuesEnumSet = {
    values: [
      { key: 'authorizatioCode', label: grantTypeLabels.authorization_code },
      { key: 'implicit', label: grantTypeLabels.implicit },
      { key: 'refreshToken', label: grantTypeLabels.refresh_token },
    ]
  }
  return [set]
})

function addGrantType(type: DataColumnValuesEnumValue) {
  const current = displayGrantTypes.value
  if (!current.includes(type.key)) {
    displayGrantTypes.value = [...current, type.key]
  }
}

function removeGrantType(type: DataColumnValuesEnumValue) {
  const current = displayGrantTypes.value
  displayGrantTypes.value = current.filter(gt => gt !== type.key)
}
</script>

<template>
  <div v-if="client" class="user-auth-tab">

    <div class="content">
      <div class="tab-section">
        <h3>{{ t('userAuth.grantTypes.title') }}</h3>
        <p class="help-text">{{ t('userAuth.helpText') }}</p>
  
        <EnumsBar
          :available-sets="availableGrantTypes"
          @add="addGrantType"
          @remove="removeGrantType"
          :selected-values="displayGrantTypes"
          :readonly="!canWrite"
        />
      </div>
  
      <div class="tab-section" :class="{ disabled: !enableUserAuth }">
        <h3>{{ t('userAuth.redirectUris.title') }}</h3>
        <p class="help-text">{{ t('userAuth.redirectUris.helpText') }}</p>
        
        <div v-if="displayRedirectUris.length === 0" class="empty-state">
          {{ t('userAuth.redirectUris.empty') }}
        </div>
        
        <div class="redirect-uris-edit">
          <div v-for="(uri, idx) in displayRedirectUris" :key="idx" class="uri-row">
            <TextInput 
              :model-value="uri" 
              @update:model-value="(val) => updateRedirectUri(idx, val)"
              :placeholder="t('userAuth.redirectUris.placeholder')"
              :readonly="!canWrite || !enableUserAuth"
            />
            <button 
              @click="removeRedirectUri(idx)" 
              class="btn-silent btn-icon" 
              :title="t('actions.remove')"
              :disabled="!canWrite || !enableUserAuth"
            >
              <IconClose />
            </button>
          </div>
          <button 
            @click="addRedirectUri" 
            :disabled="!canWrite || !enableUserAuth"
          >
            + {{ t('userAuth.redirectUris.add') }}
          </button>
        </div>
      </div>
  
      <div class="tab-section" :class="{ disabled: !enableUserAuth }">
        <h3>{{ t('userAuth.scopes.title') }}</h3>
        <p class="help-text">{{ t('userAuth.scopes.helpText') }}</p>
        
        <PermissionPicker
          v-if="client"
          v-model="displayScopes"
          :inherited-permissions="[]"
          :readonly="!canWrite || !enableUserAuth"
        />
      </div>
    </div>

    <div v-if="store.hasUserAuthChanges" class="action-bar">
      <button @click="store.saveUserAuth" class="btn-primary">{{ t('actions.saveChanges') }}</button>
      <button @click="store.discardUserAuth" class="btn-silent">{{ t('actions.discard') }}</button>
    </div>
  </div>
</template>

<style scoped>
.user-auth-tab {
  display: grid;
  grid-template-rows: auto min-content;
  min-height: 100%;
}

.content {
  display: flex;
  flex-direction: column;
}

.tab-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
}

.tab-section.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.enable-checkbox {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  cursor: pointer;
}

.enable-checkbox input[type="checkbox"] {
  width: 1.25rem;
  height: 1.25rem;
  cursor: pointer;
}

.checkbox-label {
  font-size: 1rem;
}

.help-text {
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  margin: 0;
}

h3 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
}

.empty-state {
  color: var(--color-text-secondary);
  font-style: italic;
}

.grant-types-edit label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.redirect-uris-edit,
.scopes-edit {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.uri-row,
.scope-row {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.btn-icon svg {
  width: 16px;
}

.edit-actions {
  display: flex;
  gap: 0.5rem;
  padding-top: 1rem;
}

.action-bar {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  border-top: 1px solid hsl(var(--color-80));
  background: hsl(var(--color-90));
  position: sticky;
  bottom: 0;
  box-shadow: 0 0px 20px var(--color-shadow);
}
</style>
